import { getSessionCoordinator } from "../services/sessionCoordinator.js";
import ServerError from "@/views/errors/500.vue";
import NetworkError from "@/views/errors/NetworkError.vue";
import { createApp } from "vue";
import axios from "axios";

/**
 * Check if an error is from an aborted/canceled request.
 * This handles various types of abort signals:
 * - axios CanceledError (axios v0.22+)
 * - ERR_CANCELED error code
 * - axios.isCancel() check
 * - Native AbortError from AbortController
 * - Network errors caused by request abortion (NS_BINDING_ABORTED in Firefox)
 * 
 * @param {Error} error - The error to check
 * @returns {boolean} - True if the error is from an aborted request
 */
export function isAbortedRequest(error) {
  if (!error) return false;
  
  // Check for axios CanceledError (axios v0.22+)
  if (error.name === "CanceledError") return true;
  
  // Check for ERR_CANCELED error code
  if (error.code === "ERR_CANCELED") return true;
  
  // Check using axios.isCancel (official axios method)
  if (axios.isCancel(error)) return true;
  
  // Check for native AbortError from AbortController
  if (error.name === "AbortError") return true;
  
  // Check for canceled message (common in axios cancellations)
  if (error.message === "canceled") return true;
  
  return false;
}

export function debounce(fn, delay) {
  let timerId;
  return function debounced(...args) {
    clearTimeout(timerId);
    timerId = setTimeout(() => fn.apply(this, args), delay);
  };
}

/**
 * Factory for goal achieve date calculator methods.
 *
 * Usage (inside a Vue component):
 * methods: {
 *   calculateGoalAchieveDate: createGoalAchieveDateMethod({
 *     getTarget() { return this.adding.goal.input.goal_target },
 *     getPayment() { return this.adding.goal.input.savings },
 *     getAbortController() { return this.currentGoalDateAbortController },
 *     setAbortController(controller) { this.currentGoalDateAbortController = controller },
 *     clearResults() { this.adding.nperResults = {} },
 *     setResults(res) { this.adding.nperResults = res },
 *   })
 * }
 */
export function createGoalAchieveDateMethod({
  getTarget,
  getPayment,
  getAbortController,
  setAbortController,
  clearResults,
  setResults,
}) {
  return function goalAchieveDateMethod() {
    const target = getTarget.call(this);
    const payment = getPayment.call(this);

    if (!target || !payment) {
      clearResults.call(this);
      const existingController = getAbortController.call(this);
      if (existingController) {
        existingController.abort();
        setAbortController.call(this, null);
      }
      return;
    }

    const previousController = getAbortController.call(this);
    if (previousController) {
      previousController.abort();
    }
    const controller = new AbortController();
    setAbortController.call(this, controller);

    this.$store
      .dispatch("goals/calculateGoalAchieveDate", {
        target,
        payment,
        options: { signal: controller.signal },
      })
      .then((res) => {
        if (controller.signal.aborted) return;
        setResults.call(this, res);
      })
      .catch((err) => {
        if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
          return;
        }
        throw err;
      })
      .finally(() => {
        if (getAbortController.call(this) === controller) {
          setAbortController.call(this, null);
        }
      });
  };
}

const injectGoalMonthlyAmountMobileStyles = () => {
  document.querySelectorAll(".goal-monthly-amount").forEach((element) => {
    element.closest(".modal-content")?.classList.add("goal-monthly-modal-content");
  });

  if (document.getElementById("goal-monthly-amount-mobile-style")) return;

  const style = document.createElement("style");
  style.id = "goal-monthly-amount-mobile-style";
  style.textContent = `
    @media screen and (max-width: 767px) {
      .goal-monthly-modal-content {
        padding-left: 1rem !important;
        padding-right: 1rem !important;
      }

      .goal-monthly-modal-content .goal-monthly-amount {
        padding: 1.25rem 1rem !important;
      }

      .goal-monthly-amount .numbers-wrapper,
      .goal-monthly-amount .div-block-23 {
        width: 100%;
      }

      .goal-monthly-amount .div-block-23 {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        flex-wrap: nowrap;
      }

      .goal-monthly-amount .form-block-3 {
        flex: 1 1 auto;
        min-width: 0;
      }

      .goal-monthly-amount .form-input.number-inputs {
        width: 100%;
        box-sizing: border-box;
      }

      .goal-monthly-amount .badge.is-dark-currency {
        flex: 0 0 3.25rem;
        margin-left: 0;
        padding: 0.4rem 0.35rem;
        box-sizing: border-box;
        white-space: normal;
        text-align: center;
        line-height: 1;
      }
    }
  `;
  document.head.appendChild(style);
};

export const reInitializedWebflowInteractions = () => {
  const Webflow = window.Webflow;
  //RE-INIT WF as Vue.js init breaks WF interactions
  if (Webflow) {
    Webflow.destroy();
    Webflow.ready();
    Webflow.require("ix2").init();
  }
  document.dispatchEvent(new Event("readystatechange"));
  safeInitializeDatePickerWithRetry();
  initializeEmbeddedEducation();
  initializeNumberComma();
  setNavbarValues();
  initializeToolTip();
  injectGoalMonthlyAmountMobileStyles();
  // $('.loading-screen').remove();
};
export const setNavbarValues = () => {
  const $store = window.$store;
  if ($store.getters["auth/data"].fname) {
    const elements = document.querySelectorAll(
      ".navbar_title_wrapper>.text-size-small",
    );
    elements.forEach((el) => {
      el.innerHTML = $store.getters["auth/data"].fname;
    });
  }
  const profile_element = document.getElementById("navbar_profile");
  if (profile_element) {
    profile_element.style.backgroundImage = `url('${$store.getters["auth/data"].p_image}')`;
    profile_element.style.cursor = "pointer";
    profile_element.addEventListener(
      "click",
      () => (window.location.href = "/app/account-settings"),
    );
  }
};
export const initializeEmbeddedEducation = () => {
  const elements = document.querySelectorAll(".has_embeddededucation");

  elements.forEach((element) => {
    const input = element.querySelector("input");
    const educationElement = element.querySelector(".embedded_education");

    educationElement.style.display = "none";

    input.addEventListener("focus", function () {
      educationElement.style.display = "block";
    });
  });
};
export const initializeNumberComma = () => {
  function addNumberComma(event) {
    // skip for arrow keys
    if (event.which >= 37 && event.which <= 40) {
      event.preventDefault();
    }
    const num = event.target.value.replace(/,/gi, "");
    // the following line has been simplified. Revision history contains original.
    event.target.value = num.split(/(?=(?:\d{3})+$)/).join(",");
  }

  document.querySelectorAll("input[data-type='number']").forEach((element) => {
    element.addEventListener("keyup", addNumberComma);
  });
};
export const normalizeMonthYearDate = (rawValue) => {
  const value = String(rawValue || "").trim();
  if (!value) return null;

  let match = /^(\d{4})-(\d{1,2})$/.exec(value);
  if (match) {
    const year = Number(match[1]);
    const month = Number(match[2]);
    if (month >= 1 && month <= 12) {
      return `${String(month).padStart(2, "0")}/${year}`;
    }
    return null;
  }

  match = /^(\d{1,2})\/(\d{4})$/.exec(value);
  if (match) {
    const month = Number(match[1]);
    const year = Number(match[2]);
    if (month >= 1 && month <= 12) {
      return `${String(month).padStart(2, "0")}/${year}`;
    }
    return null;
  }

  match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value);
  if (match) {
    const month = Number(match[2]);
    const year = Number(match[3]);
    if (month >= 1 && month <= 12) {
      return `${String(month).padStart(2, "0")}/${year}`;
    }
    return null;
  }

  match = /^([A-Za-z]+)\s+(\d{4})$/.exec(value);
  if (match) {
    const parsed = Date.parse(`${match[1]} 1, ${match[2]}`);
    if (!Number.isNaN(parsed)) {
      const date = new Date(parsed);
      return `${String(date.getMonth() + 1).padStart(2, "0")}/${date.getFullYear()}`;
    }
  }

  return null;
};

const toNativeMonthValue = (value) => {
  const normalized = normalizeMonthYearDate(value);
  if (!normalized) return "";
  const [month, year] = normalized.split("/");
  return `${year}-${month}`;
};

// will look into this incase it breaks other instances
export const initializeDatePicker = (
  selector = '[data-toggle="datepicker"]',
  format = "dd/mm/yyyy",
  minViewMode = "days",
) => {
  // var $ = window.$;
  const fields = $(selector);
  if (!document.getElementById("datepicker-readonly-style")) {
    const style = document.createElement("style");
    style.id = "datepicker-readonly-style";
    style.textContent = `
      input[data-toggle="datepicker"][readonly]:not([disabled]) {
        background-color: #fff !important;
        cursor: pointer;
        opacity: 1 !important;
        pointer-events: auto;
        -webkit-text-fill-color: currentColor;
      }
    `;
    document.head.appendChild(style);
  }
  if (window.innerWidth < 768 && minViewMode === "months") {
    if (typeof fields?.datepicker !== "undefined") {
      fields.datepicker("destroy");
    }
    fields.each(function () {
      const value = toNativeMonthValue(this.value || this.getAttribute("value"));
      this.type = "month";
      this.removeAttribute("readonly");
      this.value = value;
    });
    return;
  }
  if (typeof fields?.datepicker === "undefined") {
    return;
  }
  // made changes concerning the date picker.

  fields
    ?.datepicker("destroy")
    .datepicker({
      format: format,
      dateFormat: format,
      startView: 2,
      minViewMode,
      starView: minViewMode,
      date:
        document.title === "On-Boarding" 
          ? new Date().setFullYear(new Date().getFullYear() - 30)
          : new Date(),
      autoHide: true,
    });
  // Available date placeholders:
  // Year: yyyy
  // Month: mm
  // Day: dd
  if (window.innerWidth < 768) {
    $(selector)?.attr("readonly", "readonly");
  }
};
export const initializeToolTip = () => {
  // Get the tooltip element
  const tooltip1 = document.getElementById("tooltip-1");
  const tooltip2 = document.getElementById("tooltip-2");
  // Get the tooltip content element
  const tooltipContent = document.getElementById("tooltipcontent1");
  const tooltipContent2 = document.getElementById("tooltipcontent2");

  // Add click event listener to the tooltip
  tooltip1?.addEventListener("click", () => {
    // Toggle the "show" class to show or hide the tooltip content
    tooltipContent?.classList.toggle("show");
  });

  // Add click event listener to the tooltip
  tooltip2?.addEventListener("click", () => {
    // Toggle the "show" class to show or hide the tooltip content
    tooltipContent2?.classList.toggle("show");
  });
};
export const formatNumber = (number, decimals = 2) => {
  // format the number using toLocaleString()
  return Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(number);
};

/**
 * Format a date consistently across the application
 * @param {Date|string|number} date - The date to format
 * @param {string} format - The format type: 'short' (21 Jan 2026), 'long' (21 January 2026), 'numeric' (21/01/2026)
 * @returns {string} Formatted date string
 */
export const formatDate = (date, format = 'short') => {
  if (!date) return '';
  
  const dateObj = date instanceof Date ? date : new Date(date);
  
  if (isNaN(dateObj.getTime())) return '';
  
  const options = {
    short: { day: '2-digit', month: 'short', year: 'numeric' },
    long: { day: '2-digit', month: 'long', year: 'numeric' },
    numeric: { day: '2-digit', month: '2-digit', year: 'numeric' },
    monthYear: { month: 'short', year: 'numeric' },
  };
  
  return dateObj.toLocaleDateString('en-GB', options[format] || options.short);
};

/**
 * Format a currency value consistently
 * @param {number} amount - The amount to format
 * @param {string} currency - The currency code (default: 'KES')
 * @param {boolean} showDecimals - Whether to show decimal places
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = 'KES', showDecimals = true) => {
  if (amount === null || amount === undefined || isNaN(amount)) return '';
  
  const formattedAmount = Intl.NumberFormat("en-US", {
    minimumFractionDigits: showDecimals ? 2 : 0,
    maximumFractionDigits: showDecimals ? 2 : 0,
  }).format(amount);
  
  return `${currency} ${formattedAmount}`;
};

export const SUPPORTED_CURRENCIES = Object.freeze(['KES', 'USD', 'EUR', 'GBP']);

export const isSupportedCurrency = (currency) =>
  SUPPORTED_CURRENCIES.includes(currency);

export const isAssetReviewDue = (updatedAt, now = new Date()) => {
  const updated = new Date(updatedAt);
  if (Number.isNaN(updated.getTime())) return false;
  return now.getTime() - updated.getTime() >= 90 * 24 * 60 * 60 * 1000;
};

/**
 * Format a date for display in tables and lists (more compact)
 * @param {Date|string|number} date - The date to format
 * @returns {string} Formatted date string
 */
export const formatDateCompact = (date) => {
  if (!date) return '';
  
  const dateObj = date instanceof Date ? date : new Date(date);
  
  if (isNaN(dateObj.getTime())) return '';
  
  return dateObj.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

export const addValidatorMethods = (_this) => {
  $.validator.addMethod(
    "pattern",
    function (value, element, param) {
      if (this.optional(element)) {
        return true;
      }
      if (typeof param === "string") {
        param = new RegExp("^(?:" + param + ")$");
      }
      return param.test(value);
    },
    "Invalid format.",
  );

  $.validator.addMethod(
    "enforceMinAmount",
    function (value, element) {
      return this.optional(element) || parseInt(value.replaceAll(",", ""));
    },
    "Please, the minimum amount should at least be 1",
  );

  $.validator.addMethod("dateAP", function (value) {
    //var regExp = /[_\-!\"@;,.:]/;
    var regExp =
      /^(?=\d)(?:(?:31(?!.(?:0?[2469]|11))|(?:30|29)(?!.0?2)|29(?=.0?2.(?:(?:(?:1[6-9]|[2-9]\d)?(?:0[48]|[2468][048]|[13579][26])|(?:(?:16|[2468][048]|[3579][26])00)))(?:\x20|$))|(?:2[0-8]|1\d|0?[1-9]))([-.\/])(?:1[012]|0?[1-9])\1(?:1[6-9]|[2-9]\d)?\d\d(?:(?=\x20\d)\x20|$))?(((0?[1-9]|1[012])(:[0-5]\d){0,2}(\x20[AP]M))|([01]\d|2[0-3])(:[0-5]\d){1,2})?$/;
    return regExp.test(value);
  });

  $.validator.addMethod(
    "pattern",
    function (value, element, param) {
      if (this.optional(element)) {
        return true;
      }
      if (typeof param === "string") {
        param = new RegExp("^(?:" + param + ")$");
      }
      return param.test(value);
    },
    "Invalid format.",
  );

  // Validator methods for kyc and lifegoal forms
  $.validator.addMethod(
    "validGoalName",
    function (value, element) {
      return this.optional(element) || /^([a-zA-Z ]){1,50}$/.test(value);
    },
    "Please enter a valid goal name",
  );
};
export async function redirectAuthenticatedUseToCorrectRoute() {
  window.location.replace(await getSessionCoordinator().resolveRoute());
}

export function traditionallyNavigateTo(route) {
  const element = document.createElement("a");
  element.href = route;
  element.style.display = "none";
  document.body.appendChild(element);
  element.click();
}
export function formatNumberShort(num) {
  num = Number(num);
  if (isNaN(num)) return "Invalid number";
  if (num === 0) return "0";
  if (num < 1000) return num.toString();

  const suffixes = ["", "K", "M", "B", "T", "P", "E"];
  let tier = Math.floor(Math.log10(num) / 3);

  let scaled = num / Math.pow(1000, tier);
  return scaled % 1 === 0 ? scaled + suffixes[tier] : scaled.toFixed(1) + suffixes[tier];
}
export function showServerErrorModal(error) {
  console.error("Server error :", error);
  const container = document.createElement("div");
  container.id = "something-went-wrong-container";
  document.body.appendChild(container);
  document.body.style.overflow = "hidden";
  return new Promise((resolve) => {
    const app = createApp(ServerError, {
      onSuccess: () => {
        resolve(true);
      },
    });
    app.mount("#something-went-wrong-container");
  });
}

/**
 * Check if an error is a connectivity/network error.
 * This handles various types of network connectivity issues:
 * - ERR_INTERNET_DISCONNECTED (no internet connection)
 * - ERR_NETWORK (general network failure)
 * - ERR_CONNECTION_REFUSED (server not reachable)
 * - ERR_NAME_NOT_RESOLVED (DNS lookup failed)
 * - ERR_CONNECTION_TIMED_OUT (connection timeout)
 * 
 * @param {Error} error - The error to check
 * @returns {boolean} - True if the error is a connectivity error
 */
export function isConnectivityError(error) {
  if (!error) return false;
  
  const connectivityCodes = [
    "ERR_INTERNET_DISCONNECTED",
    "ERR_NETWORK",
    "ERR_CONNECTION_REFUSED",
    "ERR_NAME_NOT_RESOLVED",
    "ERR_CONNECTION_TIMED_OUT",
  ];
  
  // Check for specific error codes
  if (connectivityCodes.includes(error.code)) return true;
  
  // Check for network error in the message
  if (error.message?.toLowerCase().includes("network error")) return true;
  
  return false;
}

export function showConnectivityErrorModal(error) {
  console.error("Connectivity error:", error);
  const container = document.createElement("div");
  container.id = "connectivity-error-container";
  document.body.appendChild(container);
  document.body.style.overflow = "hidden";
  return new Promise((resolve) => {
    const app = createApp(NetworkError, {
      onSuccess: () => {
        resolve(true);
      },
    });
    app.mount("#connectivity-error-container");
  });
}

function safeInitializeDatePickerWithRetry(retries = 3, delayMs = 300) {
  const tryInit = (remaining) => {
    const $ = window.jQuery || window.$;
    if (
      typeof $ === "function" &&
      $.fn &&
      typeof $.fn.datepicker === "function"
    ) {
      initializeDatePicker();
      return;
    }
    if (remaining > 0) {
      setTimeout(() => tryInit(remaining - 1), delayMs);
    }
  };

  tryInit(retries);
}

// Briefly outline the goals table's Edit/Delete column to draw the user's
// attention to the activation toggles (e.g. after "Reactivate goals →" or a
// dashboard redirect). The flex-built "table" has no column element, so the
// column is reconstructed cell-by-cell: the last cell of each header row +
// the .receipt_col wrapping each row's .goal-actions. The outline is drawn
// with directional box-shadows so adjacent cells form one continuous
// rectangle — top+sides on the header, sides only on middle cells,
// bottom+sides on the last cell. On the mobile card layout (no .receipt_col)
// each .goal-actions gets a simple full-box flash instead. Shared by
// YourPlan/script.js and the global handler in main.js so both behave
// identically.
export function flashGoalsEditColumn(fallbackEl) {
  const applied = [];
  const tag = (el, ...classes) => {
    if (!el) return;
    el.classList.add(...classes);
    applied.push([el, classes]);
  };

  const column = [];
  document.querySelectorAll(".table10_header-row").forEach((hr) => {
    const cells = hr.querySelectorAll(".receipt_col");
    if (cells.length) column.push(cells[cells.length - 1]);
  });
  document.querySelectorAll(".goal-actions").forEach((ga) => {
    const cell = ga.closest(".receipt_col");
    if (cell) column.push(cell);
    else tag(ga, "goals-attention-flash"); // mobile cards: simple full box
  });

  column.forEach((el, i) => {
    const isHead = i === 0;
    const isTail = i === column.length - 1;
    const classes = ["goals-col-flash"];
    if (isHead) classes.push("goals-col-flash--head");
    if (isTail) classes.push("goals-col-flash--tail");
    tag(el, ...classes);
  });

  // Nothing rendered (no header, no rows) — fall back to a whole-area flash.
  if (!applied.length && fallbackEl) tag(fallbackEl, "goals-attention-flash");

  window.setTimeout(() => {
    applied.forEach(([el, classes]) => el.classList.remove(...classes));
  }, 2400);
}

// A goal's `active` flag arrives from the backend in inconsistent shapes
// across instances (boolean, 1/0, "true"/"false", "yes"/"no", ""). Normalise
// to a single source of truth so the contribution total and the paused
// gating can never disagree with the toggle the user actually sees.
export function isGoalActive(goal) {
  if (!goal) return false;
  const a = goal.active;
  if (a === false || a === 0 || a === null || a === undefined) return false;
  if (typeof a === "string") {
    const s = a.trim().toLowerCase();
    return !(s === "" || s === "false" || s === "0" || s === "no");
  }
  return Boolean(a);
}

// Sum the monthly contribution of every ACTIVE goal, using the exact figure
// rendered in the "Monthly investment" column (allocation_int_desc) so the
// total is provably "the sum of the numbers the user sees". We deliberately
// avoid goal.allocation_int — that field is a ratio (0–1) in the
// make-investment payload and an amount here; allocation_int_desc is
// unambiguous. Goal selection mirrors exactly the rows the table renders:
// goals_ff (when populated), goals_ef (when ef === "yes"), and every entry
// in goals_cf_long / goals_cf_short. Returns { hasGoals, total } so callers
// can tell "no goals yet" (don't gate / don't flash) apart from "every goal
// is paused" (total 0 with goals present).
export function summariseActiveGoals(plan) {
  const result = { hasGoals: false, total: 0 };
  if (!plan) return result;

  const goals = [];
  if (plan.goals_ff && Object.keys(plan.goals_ff).length > 0) {
    goals.push(plan.goals_ff);
  }
  if (plan.goals_ef && plan.goals_ef.ef === "yes") {
    goals.push(plan.goals_ef);
  }
  if (Array.isArray(plan.goals_cf_long)) goals.push(...plan.goals_cf_long);
  if (Array.isArray(plan.goals_cf_short)) goals.push(...plan.goals_cf_short);

  result.hasGoals = goals.length > 0;
  result.total = goals.reduce((sum, goal) => {
    if (!isGoalActive(goal)) return sum;
    const raw = goal.allocation_int_desc;
    const n = parseFloat(String(raw == null ? "" : raw).replace(/,/g, ""));
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);
  return result;
}
